import { DailyGuest, Invoice } from '../types';

import { guestStayDays } from './dailyGuestDates';



const GUEST_STAY_NOTE_PREFIX = 'guestStay:';



export type GuestInvoiceMeta = {

  dailyRate: number;

  days: number;

  totalDue: number;

  checkInDate?: string;

  checkOutDate?: string;

};



export type GuestStayContext = Pick<DailyGuest, 'dailyRate' | 'checkInDate' | 'checkOutDate'>;



function num(v: unknown): number {

  const n = Number(v);

  return Number.isFinite(n) ? n : 0;

}



function parseInrAmount(s: unknown): number {

  const cleaned = String(s ?? '').replace(/[₹,\s]/g, '');

  const n = Number(cleaned);

  return Number.isFinite(n) ? n : 0;

}



export function formatGuestMoney(amount: number): string {

  return `₹${num(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

}



export function isGuestStayInvoice(invoice: Invoice): boolean {

  const t = String(invoice.paymentType || '').toUpperCase();

  if (t === 'GUEST_STAY') return true;

  return String(invoice.note || '').startsWith(GUEST_STAY_NOTE_PREFIX);

}



export function parseGuestStayInvoiceNote(note?: string | null): {

  stayId: number;

  meta: GuestInvoiceMeta | null;

} | null {

  const s = String(note || '');

  if (!s.startsWith(GUEST_STAY_NOTE_PREFIX)) return null;

  const rest = s.slice(GUEST_STAY_NOTE_PREFIX.length);

  const pipe = rest.indexOf('|');

  const idPart = pipe >= 0 ? rest.slice(0, pipe) : rest;

  const stayId = parseInt(idPart, 10);

  if (!Number.isFinite(stayId)) return null;

  let meta: GuestInvoiceMeta | null = null;

  if (pipe >= 0) {

    try {

      meta = normalizeGuestMeta(JSON.parse(rest.slice(pipe + 1)) as GuestInvoiceMeta);

    } catch {

      meta = null;

    }

  }

  return { stayId, meta };

}



function normalizeGuestMeta(m: Partial<GuestInvoiceMeta> | null): GuestInvoiceMeta | null {

  if (!m) return null;

  const dailyRate = num(m.dailyRate);

  const days = Math.max(1, Math.round(num(m.days)));

  const totalDue = num(m.totalDue);

  if (dailyRate <= 0 && days <= 0 && totalDue <= 0) return null;

  return {

    dailyRate,

    days: days > 0 ? days : 1,

    totalDue: totalDue > 0 ? totalDue : Math.round(dailyRate * Math.max(1, days) * 100) / 100,

    checkInDate: m.checkInDate,

    checkOutDate: m.checkOutDate,

  };

}



export function metaFromPaymentLabels(

  paymentMonth?: string | null,

  paymentYear?: string | null

): GuestInvoiceMeta | null {

  const month = String(paymentMonth || '');

  const year = String(paymentYear || '');

  let dailyRate = 0;

  let days = 0;

  let totalDue = 0;



  const rateDays = month.match(/₹?\s*([\d,]+(?:\.\d+)?)\s*\/?\s*day\s*[×x]\s*(\d+)\s*day/i);

  if (rateDays) {

    dailyRate = parseInrAmount(rateDays[1]);

    days = Math.max(1, parseInt(rateDays[2], 10) || 1);

  }



  const totalMatch = year.match(/(?:stay\s+)?total\s+₹?\s*([\d,]+(?:\.\d+)?)/i);

  if (totalMatch) totalDue = parseInrAmount(totalMatch[1]);



  if (!totalDue && dailyRate > 0 && days > 0) {

    totalDue = Math.round(dailyRate * days * 100) / 100;

  }

  if (!dailyRate && totalDue > 0 && days > 0) {

    dailyRate = Math.round((totalDue / days) * 100) / 100;

  }



  return normalizeGuestMeta({ dailyRate, days, totalDue });

}



function stayFinFromGuest(stay: GuestStayContext | null | undefined) {

  if (!stay) return null;

  const days = guestStayDays(stay.checkInDate, stay.checkOutDate);

  const dailyRate = num(stay.dailyRate);

  const totalDue = Math.round(days * dailyRate * 100) / 100;

  return normalizeGuestMeta({

    dailyRate,

    days,

    totalDue,

    checkInDate: stay.checkInDate,

    checkOutDate: stay.checkOutDate,

  });

}



export function resolveGuestInvoiceMeta(

  invoice: Invoice,

  stay?: GuestStayContext | null

): GuestInvoiceMeta | null {

  let meta = parseGuestStayInvoiceNote(invoice.note)?.meta ?? null;

  if (meta && meta.dailyRate > 0 && meta.days > 0) return meta;



  const fromLabels = metaFromPaymentLabels(invoice.paymentMonth, invoice.paymentYear);

  if (fromLabels) {

    meta = normalizeGuestMeta({

      ...meta,

      ...fromLabels,

      checkInDate: meta?.checkInDate || stay?.checkInDate,

      checkOutDate: meta?.checkOutDate || stay?.checkOutDate,

    });

    if (meta && meta.dailyRate > 0) return meta;

  }



  const fromStay = stayFinFromGuest(stay);

  if (fromStay) return fromStay;



  const paid = num(invoice.paidAmount);

  const balance = num(invoice.balanceAmount);

  const totalDue = meta && meta.totalDue > 0 ? meta.totalDue : paid + balance;

  if (totalDue > 0) {

    return normalizeGuestMeta({

      dailyRate: meta?.dailyRate && meta.dailyRate > 0 ? meta.dailyRate : totalDue,

      days: meta?.days && meta.days > 0 ? meta.days : 1,

      totalDue,

      checkInDate: meta?.checkInDate || stay?.checkInDate,

      checkOutDate: meta?.checkOutDate || stay?.checkOutDate,

    });

  }



  return meta;

}



export function guestStayMetaFromInvoice(

  invoice: Invoice,

  stay?: GuestStayContext | null

): GuestInvoiceMeta | null {

  return resolveGuestInvoiceMeta(invoice, stay);

}



export function guestInvoicePeriodLabel(

  invoice: Invoice,

  stay?: GuestStayContext | null

): { line1: string; line2: string } {

  const meta = resolveGuestInvoiceMeta(invoice, stay);

  if (!meta) {

    return {

      line1: String(invoice.paymentMonth || 'Daily stay').trim() || 'Daily stay',

      line2: String(invoice.paymentYear || '').trim(),

    };

  }

  return {

    line1: `${formatGuestMoney(meta.dailyRate)}/day × ${meta.days} day(s)`,

    line2: `Total ${formatGuestMoney(meta.totalDue)}`,

  };

}



export function guestInvoiceSummary(invoice: Invoice, stay?: GuestStayContext | null) {

  const meta = resolveGuestInvoiceMeta(invoice, stay);

  const paid = num(invoice.paidAmount);

  const balance = num(invoice.balanceAmount);

  const totalDue = meta && meta.totalDue > 0 ? meta.totalDue : paid + balance;

  return {

    meta,

    dailyRate: meta?.dailyRate ?? 0,

    days: meta?.days ?? 0,

    totalDue,

    paid,

    balance,

  };

}


